import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

const AttendanceModal = ({ isOpen, onClose, eventId, onComplete }) => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [eventDetails, setEventDetails] = useState(null);
  const [attendance, setAttendance] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [whichLesson, setWhichLesson] = useState('');
  const [howDidItGo, setHowDidItGo] = useState('');
  const [anyIssues, setAnyIssues] = useState('');
  const [showForceComplete, setShowForceComplete] = useState(false);

  useEffect(() => {
    if (isOpen && eventId) {
      fetchEventDetails();
      fetchStudents();
    }
  }, [isOpen, eventId]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (isOpen && e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscapeKey, true);
    
    return () => {
      document.removeEventListener('keydown', handleEscapeKey, true);
    };
  }, [isOpen, onClose]);

  const fetchEventDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/calendar/' + eventId, {
        headers: {
          'x-auth-token': token
        }
      });
      setEventDetails(response.data);
    } catch (error) {
      console.error('Error fetching event details:', error);
      setError('Failed to load event details');
    }
  };

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/calendar/' + eventId + '/students', {
        headers: {
          'x-auth-token': token
        }
      });
      
      const studentList = response.data.students || [];
      setStudents(studentList);
      
      // Initialize attendance data with empty status (not pre-filled)
      const initialAttendance = {};
      studentList.forEach(student => {
        initialAttendance[student.deal_id] = { status: '', notes: '' };
      });
      
      setAttendance(initialAttendance);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching students:', error);
      setError('Failed to load enrolled students');
      setLoading(false);
    }
  };

  const handleAttendanceChange = (studentId, status) => {
    // Ensure we're only storing primitive values to avoid circular references
    setAttendance(prev => ({
      ...prev,
      [studentId]: { 
        status: String(status),
        notes: prev[studentId]?.notes || ''
      }
    }));
  };

  const handleNotesChange = (studentId, notes) => {
    // Ensure we're only storing primitive values to avoid circular references
    setAttendance(prev => ({
      ...prev,
      [studentId]: { 
        status: prev[studentId]?.status || 'present',
        notes: String(notes)
      }
    }));
  };

  const handleWhichLesson = (e) => {
    setWhichLesson(e.target.value);
  };
  
  const handleHowDidItGo = (e) => {
    setHowDidItGo(e.target.value);
  };
  
  const handleAnyIssues = (e) => {
    setAnyIssues(e.target.value);
  };

  const handleSubmit = async (forceComplete = false) => {
    try {
      setSubmitting(true);
      setError('');
      const token = localStorage.getItem('token');
      
      // Create a safe copy of attendance data to avoid circular references
      const safeAttendanceData = {};
      
      // Only check attendance if there are students enrolled
      if (students.length > 0) {
        // Check if all students have attendance status selected
        let missingAttendance = false;
        
        Object.keys(attendance).forEach(studentId => {
          // Ensure we only have primitive values
          const status = attendance[studentId]?.status || '';
          if (!status) {
            missingAttendance = true;
          }
          
          safeAttendanceData[studentId] = {
            status: String(status),
            notes: String(attendance[studentId]?.notes || '')
          };
        });
        
        // Check if required attendance is filled
        if (missingAttendance) {
          setError('Please select attendance status (Present/Absent/Late) for all students.');
          setSubmitting(false);
          return;
        }
      }
      
      // Check if required fields are filled
      if (!whichLesson.trim() || !howDidItGo.trim() || !anyIssues.trim()) {
        setError('All feedback fields are required. Please fill them in.');
        setSubmitting(false);
        return;
      }
      
      const combinedNotes = `Which lesson: ${whichLesson}\nHow did it go: ${howDidItGo}\nAny issues: ${anyIssues}`;
      
      await axios.patch('/api/calendar/' + eventId + '/complete', 
        { 
          attendance_data: safeAttendanceData,
          lesson_notes: combinedNotes,
          force_complete: Boolean(forceComplete)
        }, 
        {
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': token
          }
        });
      
      // Call the onComplete callback to notify parent component
      onComplete();
      
    } catch (error) {
      console.error('Error completing lesson with attendance:', error);
      console.error('Error details:', error.response?.data);
      
      // Check if this is a timing error
      if (error.response?.data?.code === 'LESSON_NOT_ENDED') {
        const eventEndTime = new Date(error.response.data.event_end_time);
        setError(`This lesson cannot be marked as completed until after its scheduled end time: ${eventEndTime.toLocaleString()}`);
      } else if (error.response?.data?.code === 'LESSON_NOT_ENDED_ADMIN') {
        // Show force complete option for admins
        setError('This lesson is scheduled in the future. Do you still want to mark it as completed?');
        setShowForceComplete(true);
      } else if (error.response?.data?.code === 'SYNTAX_ERROR') {
        // Handle JSON syntax errors
        setError(`Error in data format: ${error.response?.data?.details || 'Invalid format'}`);
      } else if (error.response?.data?.details) {
        // Show detailed error if available
        setError(`${error.response?.data?.message}: ${error.response?.data?.details}`);
      } else {
        setError(error.response?.data?.message || 'Failed to save attendance data');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
      onClick={(e) => {
        // Close when clicking outside the modal
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-lg shadow-lg max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-medium">Take Attendance</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="px-6 py-4 flex-1 overflow-auto">
          {/* Event Information */}
          {eventDetails && (
            <div className="mb-6">
              <h4 className="font-medium">{eventDetails.title}</h4>
              <div className="text-sm text-gray-600">
                Date: {new Date(eventDetails.start_time).toLocaleDateString()} {new Date(eventDetails.start_time).toLocaleTimeString()} - {new Date(eventDetails.end_time).toLocaleTimeString()}
              </div>
              {eventDetails.course_name && (
                <div className="text-sm text-gray-600">
                  Course: {eventDetails.course_name} ({eventDetails.course_age_group}, {eventDetails.course_level})
                </div>
              )}
              {eventDetails.tutor_name && (
                <div className="text-sm text-gray-600">
                  Tutor: {eventDetails.tutor_name}
                </div>
              )}
            </div>
          )}
          
          {/* Required Feedback Fields */}
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Which lesson did you do? <span className="text-red-500">*</span>
              </label>
              <textarea
                value={whichLesson}
                onChange={handleWhichLesson}
                rows={2}
                className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 rounded-md"
                placeholder="Describe which lesson you completed today..."
                required
              ></textarea>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                How did it go? <span className="text-red-500">*</span>
              </label>
              <textarea
                value={howDidItGo}
                onChange={handleHowDidItGo}
                rows={2}
                className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 rounded-md"
                placeholder="Describe how the lesson went..."
                required
              ></textarea>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Any issues? <span className="text-red-500">*</span>
              </label>
              <textarea
                value={anyIssues}
                onChange={handleAnyIssues}
                rows={2}
                className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 rounded-md"
                placeholder="Describe any issues or challenges encountered..."
                required
              ></textarea>
            </div>
          </div>
          
          {/* Error Message */}
          {error && (
            <div className={`mb-4 text-sm p-2 border rounded ${
              showForceComplete 
                ? "bg-yellow-50 border-yellow-200 text-yellow-800" 
                : "bg-red-50 border-red-200 text-red-500"
            }`}>
              <p>{error}</p>
              
              {/* For admin override */}
              {showForceComplete && (
                <div className="mt-2 flex justify-end">
                  <button
                    onClick={() => handleSubmit(true)}
                    className="px-2 py-1 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700 ml-2"
                  >
                    Force Complete Anyway
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* No students message */}
          {!loading && students.length === 0 && !error && (
            <div className="mb-4 text-sm p-3 border rounded bg-blue-50 border-blue-200 text-blue-800">
              <p>This class has no enrolled students. You can still mark it as completed without attendance data.</p>
            </div>
          )}
          
          {/* Student Attendance */}
          <div>
            <h4 className="text-md font-medium mb-2">Record Attendance</h4>
            {loading ? (
              <div className="text-center py-4">
                <svg className="animate-spin h-6 w-6 mx-auto text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            ) : students.length === 0 ? (
              <div className="text-center py-4 text-gray-500">No students enrolled in this class</div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="min-w-full bg-white">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="py-2 px-4 border-b text-left">Student</th>
                      <th className="py-2 px-4 border-b text-left">Attendance</th>
                      <th className="py-2 px-4 border-b text-left">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map(student => (
                      <tr key={student.id}>
                        <td className="py-2 px-4 border-b">
                          {student.student_name || student.lead_name || 'Unknown'}
                        </td>
                        <td className="py-2 px-4 border-b">
                          <div className="flex space-x-3">
                            <label className="inline-flex items-center">
                              <input
                                type="radio"
                                className="form-radio h-4 w-4 text-green-600"
                                name={`attendance-${student.deal_id}`}
                                value="present"
                                checked={attendance[student.deal_id]?.status === 'present'}
                                onChange={() => handleAttendanceChange(student.deal_id, 'present')}
                                required
                              />
                              <span className="ml-2 text-sm text-gray-700">Present</span>
                            </label>
                            <label className="inline-flex items-center">
                              <input
                                type="radio"
                                className="form-radio h-4 w-4 text-red-600"
                                name={`attendance-${student.deal_id}`}
                                value="absent"
                                checked={attendance[student.deal_id]?.status === 'absent'}
                                onChange={() => handleAttendanceChange(student.deal_id, 'absent')}
                              />
                              <span className="ml-2 text-sm text-gray-700">Absent</span>
                            </label>
                            <label className="inline-flex items-center">
                              <input
                                type="radio"
                                className="form-radio h-4 w-4 text-yellow-600"
                                name={`attendance-${student.deal_id}`}
                                value="late"
                                checked={attendance[student.deal_id]?.status === 'late'}
                                onChange={() => handleAttendanceChange(student.deal_id, 'late')}
                              />
                              <span className="ml-2 text-sm text-gray-700">Late</span>
                            </label>
                          </div>
                        </td>
                        <td className="py-2 px-4 border-b">
                          <input
                            type="text"
                            className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 rounded-md"
                            placeholder="Notes for this student"
                            value={attendance[student.deal_id]?.notes || ''}
                            onChange={(e) => handleNotesChange(student.deal_id, e.target.value)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
        
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || loading}
            className={`px-4 py-2 rounded ${submitting || loading ? 'bg-gray-300 text-gray-500' : 'bg-green-600 text-white hover:bg-green-700'}`}
          >
            {submitting ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </span>
            ) : (
              'Complete Lesson'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

AttendanceModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onComplete: PropTypes.func.isRequired
};

export default AttendanceModal;